import { useQuery } from '@tanstack/react-query';
import { ApiError, i18nCore } from '@pro4-erp/core-web';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserSettings } from '../types';
import { getUserSettingsForMe } from '../api/getUserSettingsForMe';
import { ErrorCard } from '@/common';
import DefaultUserConnectionForm from '../components/DefaultUserConnectionForm';
import DefaultUserConnectionFormSkeleton from '../skeletons/DefaultUserConnectionFormSkeleton';
import { availableLanguages } from '@/core';

const PageLayout = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ px: '1rem' }}>
      <Typography variant="h4">{t('User settings', { defaultValue: 'User settings' })}</Typography>
      {children}
    </Box>
  );
};

const Page = () => {
  const { t } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem('language') || 'en-US'
  );

  const query = useQuery<UserSettings, ApiError>({
    queryKey: ['user-settings'],
    queryFn: getUserSettingsForMe,
    retry: false,
  });

  if (query.isError) {
    return (
      <PageLayout>
        <ErrorCard error={query.error} onRetryClick={() => query.refetch()} />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Box sx={{ mt: '0.5rem' }}>
        {query.isLoading ? (
          <DefaultUserConnectionFormSkeleton />
        ) : (
          <DefaultUserConnectionForm userSettings={query.data} />
        )}
      </Box>
      <Box sx={{ mt: '1.5rem' }}>
        <Typography variant="h5">{t('Language', { defaultValue: 'Language' })}</Typography>
        <Autocomplete
          sx={{ mt: '0.5rem' }}
          value={currentLanguage}
          onChange={(_, newValue: string | null) => {
            if (!newValue) {
              setCurrentLanguage('en-US');
              localStorage.setItem('language', 'en-US');
              i18nCore.changeLanguage('en-US');
              return;
            }

            setCurrentLanguage(newValue);
            localStorage.setItem('language', newValue);
            i18nCore.changeLanguage(newValue);
          }}
          options={availableLanguages}
          fullWidth
          getOptionLabel={(e) => e}
          isOptionEqualToValue={(option, value) => option === value}
          renderInput={(inputParams) => (
            <TextField
              {...inputParams}
              InputLabelProps={{}} // Added to fix typeerror
              label={t('Language', { defaultValue: 'Language' })}
            />
          )}
        />
      </Box>
      {/* <Box sx={{ mt: '1.5rem' }}>
        {query.isLoading ? (
          <ConnectionInfoFormSkeleton />
        ) : (
          <ConnectionInfoForm userSettings={query.data} />
        )}
      </Box> */}
    </PageLayout>
  );
};

export default Page;
